import { intentStatusSchema } from "@redotech/redo-conversion-settings-common/predictive-conversion-settings";
import * as z from "zod";

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
  .refine((value) => {
    // Reject shape-valid but impossible dates (e.g. 2026-02-31) before they
    // reach Temporal.PlainDate.from(..) in the handler, which would otherwise
    // silently constrain them. Round-trip through UTC and require the parts to
    // survive unchanged.
    const [year, month, day] = value.split("-").map(Number) as [
      number,
      number,
      number,
    ];
    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }, "Not a real calendar date");

/** Whether the merchant's Shopify app is still installed (has an enabled integration). */
export const intentInstallStatusSchema = z.enum(["installed", "churned"]);
export type IntentInstallStatus = z.infer<typeof intentInstallStatusSchema>;

/**
 * Free-trial lifecycle derived from the chop billing `startDate` (which doubles
 * as the trial-end timestamp), plus the Shopify usage-plan check:
 * - `active`  — startDate is in the future; still inside the free trial.
 * - `paying`  — trial ended and the merchant accepted a Shopify usage plan
 *   (converted). Same signal as `isIntentTrialExpiredUnpaid` treats as paid.
 * - `lapsed`  — trial ended with no usage plan on record (expired unpaid).
 * - `none`    — no billing startDate on record yet.
 */
export const intentTrialStateSchema = z.enum([
  "active",
  "paying",
  "lapsed",
  "none",
]);
export type IntentTrialState = z.infer<typeof intentTrialStateSchema>;

export const intentSelfServeTrialRowSchema = z.object({
  teamId: z.string(),
  name: z.string(),
  storeUrl: z.string(),
  installStatus: intentInstallStatusSchema,
  trialState: intentTrialStateSchema,
  /** Whole days until the trial ends. Null unless `trialState === "active"`. */
  trialDaysLeft: z.number().nullable(),
  intentStatus: intentStatusSchema,
  /** Stored merchant-size assumption; null when never configured. */
  annualOrders: z.number().nullable(),
  /** Rescue-panel metrics over the window; null when the team has no data in range. */
  impressions: z.number().nullable(),
  engagements: z.number().nullable(),
  rescueOrders: z.number().nullable(),
  recoveredRevenue: z.number().nullable(),
  currency: z.string().nullable(),
});
export type IntentSelfServeTrialRow = z.infer<
  typeof intentSelfServeTrialRowSchema
>;

export const getIntentSelfServeTrials = {
  name: "getIntentSelfServeTrials" as const,
  input: z
    .object({ startDate: isoDate, endDate: isoDate })
    .refine(({ startDate, endDate }) => startDate <= endDate, {
      message: "startDate must be on or before endDate",
      path: ["startDate"],
    }),
  output: z.object({
    rows: z.array(intentSelfServeTrialRowSchema),
    /** Total intent-only merchants (row count). */
    merchantsTotal: z.number(),
    /** Merchants whose trial is still active. */
    activeTrials: z.number(),
    totalRescueOrders: z.number(),
    /**
     * Raw sum of per-merchant recovered revenue. NOT FX-normalized — each
     * merchant's `recoveredRevenue` is in its own `currency`, so this is a
     * cross-currency total (not USD). The per-row currency is preserved on
     * each row; the UI renders this as a plain number, not a USD figure.
     */
    totalRecoveredRevenue: z.number(),
    /** Merchants with any rescue-panel data in range. */
    metricsReporting: z.number(),
  }),
};
